import {
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import type {
    HeaderScrollEdgeConstantType,
    HeaderDirectionConstantType,
    HeaderHeightConstantType,
    HeaderDynamicStyleType,
    HeaderScrollEdgeKeys,
    HeaderScrollEdge
} from "./type";
import {
    type Mutable
} from "../../types";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    webStyle
} from "../../utils";

export const HEADER_SCROLL_EDGES: Record<HeaderScrollEdge, HeaderScrollEdgeKeys> = {
    rest: {
        isSeparatorVisible: false,
        level: "level0"
    },
    scrolled: {
        isSeparatorVisible: true,
        level: "level1"
    },
    static: {
        isSeparatorVisible: true,
        level: "level0"
    }
};

export const getHeaderHeight = ({
    controlHeights,
    spaces
}: HeaderHeightConstantType): number => {
    return controlHeights.large + spaces.spacingSm * 2;
};

export const getHeaderDirection = ({
    direction
}: HeaderDirectionConstantType): ViewStyle => {
    if(Platform.OS === "web") {
        return {};
    }

    return {
        direction
    };
};

export const getHeaderScrollEdge = ({
    isScrolled
}: HeaderScrollEdgeConstantType): HeaderScrollEdge => {
    if(isScrolled === undefined) {
        return "static";
    }

    return isScrolled ? "scrolled" : "rest";
};

const stylesheet = StyleSheet.create({
    startSlot: {
        justifyContent: "flex-start"
    },
    endSlot: {
        justifyContent: "flex-end"
    },
    container: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
        ...webStyle({
            userSelect: "none"
        })
    },
    sideSlot: {
        flexDirection: "row",
        alignItems: "center",
        flexBasis: 0,
        flexGrow: 1
    },
    slotContent: {
        flexDirection: "row",
        alignItems: "center"
    },
    title: {
        textAlign: "center"
    },
    titleSlot: {
        flexShrink: 1
    }
});

export const useStyles = ({
    safeAreaViewBackgroundColor,
    isWorkWithHeaderSpace,
    backgroundColor,
    controlHeights,
    shapeConfigs,
    sideMinWidth,
    headerSpace,
    shadowAlpha,
    elevations,
    isScrolled,
    direction,
    borders,
    configs,
    colors,
    spaces
}: HeaderDynamicStyleType) => {
    const currentScrollEdge = HEADER_SCROLL_EDGES[getHeaderScrollEdge({
        isScrolled
    })];

    const currentHeaderSpace = isWorkWithHeaderSpace ? headerSpace ?? configs.headerSpace : 0;

    const styles = {
        container: {
            borderBottomColor: currentScrollEdge.isSeparatorVisible ? colors.content.border.subtle : "transparent",
            backgroundColor: colors.content.container[backgroundColor],
            borderBottomWidth: borders.separator,
            paddingHorizontal: spaces.spacingSm,
            zIndex: shapeConfigs.layers.raised,
            paddingTop: currentHeaderSpace,
            minHeight: getHeaderHeight({
                controlHeights,
                spaces
            }) + currentHeaderSpace,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        safeAreaView: {
            backgroundColor: colors.content.container[safeAreaViewBackgroundColor ?? backgroundColor]
        } as Mutable<ViewStyle>,
        sideSlot: {
            minWidth: sideMinWidth
        } as Mutable<ViewStyle>,
        slotContent: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>
    };

    if(elevations[currentScrollEdge.level].length) {
        Object.assign(styles.container, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: currentScrollEdge.level,
            shadowAlpha,
            elevations
        }));
    }

    Object.assign(styles.container, getHeaderDirection({
        direction
    }));

    return styles;
};
export default stylesheet;
